<script setup lang="ts">
/* =============================================================
   BOOKING — CONFIRMATION
   =============================================================
   The "basic confirmation or receipt" from the brief: a document
   River Atlas issues on behalf of the berth owner, laid out like
   an invoice so it can be printed or saved as a PDF and filed.

   It is a demo document. The VAT line is arithmetic on the net
   price at a flat 19 %, not tax advice, and nothing is paid
   through the platform — the small print says both.

   Printing: the page carries its own print stylesheet (the
   unscoped block at the end) that hides the app's sidebar and
   top bar. It is keyed on `body:has(.receipt)`, so it applies
   only while this page is on screen, even though Vue leaves the
   style in the document after navigating away.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Alert from '@/components/ui/Alert.vue'
import Button from '@/components/ui/Button.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import Logo from '@/components/Logo.vue'
import { appConfig } from '@/config/app.config'
import { routes, to } from '@/config/routes'
import { getBooking } from '@/lib/bookings'
import { getBerth } from '@/lib/berths'
import { getCompany } from '@/lib/companies'
import { useCountries } from '@/lib/countries'
import { bookingState, formatDateTime, formatDuration, formatEur } from '@/lib/bookingLabels'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { errorMessage } from '@/lib/errors'
import type { Berth, Booking, Company } from '@/lib/types'

const VAT_RATE = 0.19

const route = useRoute()
const { countryName } = useCountries()

const booking = ref<Booking | null>(null)
const berth = ref<Berth | null>(null)
const owner = ref<Company | null>(null)
const booker = ref<Company | null>(null)
const loading = ref(true)
const loadError = ref('')

const id = computed(() => Number(route.params.id))
useDocumentTitle(() => (booking.value ? `Confirmation ${booking.value.reference}` : 'Confirmation'))

onMounted(async () => {
  try {
    const b = await getBooking(id.value)
    booking.value = b
    if (!b) return
    const fullBerth = await getBerth(b.berth_id)
    berth.value = fullBerth
    // Both parties' full records, for the address block. Either may fail on
    // a policy the reader does not pass; the document still prints without.
    const [o, c] = await Promise.all([
      fullBerth ? getCompany(fullBerth.company_id).catch(() => null) : Promise.resolve(null),
      getCompany(b.company_id).catch(() => null),
    ])
    owner.value = o
    booker.value = c
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load the booking.')
  } finally {
    loading.value = false
  }
})

const state = computed(() => (booking.value ? bookingState(booking.value) : null))
const issuable = computed(() => state.value === 'confirmed' || state.value === 'completed')

const net = computed(() => booking.value?.price_eur ?? 0)
const vat = computed(() => Math.round(net.value * VAT_RATE * 100) / 100)
const gross = computed(() => net.value + vat.value)

const dateOnly = new Intl.DateTimeFormat('en-GB', { dateStyle: 'long' })
/** The day the stay was agreed: decided for a manual berth, booked for an instant one. */
const issued = computed(() => {
  const b = booking.value
  return b ? dateOnly.format(new Date(b.decided_at ?? b.created_at)) : ''
})

const ownerName = computed(() => owner.value?.name ?? berth.value?.company?.name ?? 'The berth owner')
const bookerName = computed(() => booker.value?.name ?? booking.value?.company?.name ?? '—')

function print(): void {
  window.print()
}
</script>

<template>
  <p v-if="loading" class="page-status">Loading…</p>

  <template v-else-if="booking">
    <div class="toolbar noPrint">
      <Button variant="ghost" icon="chevronRight" :to="to.booking(booking.id)" class="back-link">
        Back to booking
      </Button>
      <Button v-if="issuable" @click="print">Print / save as PDF</Button>
    </div>

    <Alert v-if="!issuable" tone="warning" class="page-alert">
      A confirmation is issued once the booking is confirmed. This one is
      {{ state === 'requested' ? 'still waiting for the berth owner' : state }}.
    </Alert>

    <article v-else class="receipt">
      <header class="head">
        <div class="brand">
          <Logo :size="28" />
        </div>
        <div class="title">
          <h1>Booking confirmation</h1>
          <p class="ref">{{ booking.reference }}</p>
        </div>
      </header>

      <section class="parties">
        <div>
          <p class="caption">Issued by</p>
          <p class="name">{{ appConfig.name }}</p>
          <p>on behalf of <strong>{{ ownerName }}</strong></p>
          <p v-if="owner" class="muted">{{ countryName(owner.country_code) }}</p>
          <p v-if="owner?.vat_number" class="muted">VAT {{ owner.vat_number }}</p>
          <p v-if="owner" class="muted">{{ owner.contact_email }}</p>
        </div>
        <div>
          <p class="caption">Issued to</p>
          <p class="name">{{ bookerName }}</p>
          <p v-if="booker" class="muted">{{ countryName(booker.country_code) }}</p>
          <p v-if="booker?.vat_number" class="muted">VAT {{ booker.vat_number }}</p>
          <p v-if="booker" class="muted">{{ booker.contact_email }}</p>
        </div>
        <dl class="meta">
          <div>
            <dt>Reference</dt>
            <dd>{{ booking.reference }}</dd>
          </div>
          <div>
            <dt>Date</dt>
            <dd>{{ issued }}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{{ state === 'completed' ? 'Completed' : 'Confirmed' }}</dd>
          </div>
        </dl>
      </section>

      <table class="lines">
        <thead>
          <tr>
            <th scope="col">Description</th>
            <th scope="col" class="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <p class="item">
                Mooring fee — {{ booking.berth ? `${booking.berth.name}, ${booking.berth.city}` : 'berth' }}
              </p>
              <p class="muted">
                {{ formatDateTime(booking.arrival) }} – {{ formatDateTime(booking.departure) }}
                ({{ formatDuration(booking.arrival, booking.departure) }})
              </p>
              <p v-if="booking.vessel" class="muted">
                Vessel: {{ booking.vessel.name }}, {{ booking.vessel.length_m }} m
              </p>
            </td>
            <td class="num">{{ formatEur(net) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Net</th>
            <td class="num">{{ formatEur(net) }}</td>
          </tr>
          <tr>
            <th scope="row">VAT 19 %</th>
            <td class="num">{{ formatEur(vat) }}</td>
          </tr>
          <tr class="total">
            <th scope="row">Total</th>
            <td class="num">{{ formatEur(gross) }}</td>
          </tr>
        </tfoot>
      </table>

      <footer class="foot">
        <p><strong>Payment</strong> is not handled in this demo.</p>
        <p class="small">
          Demo document — not a tax invoice. Issued by {{ appConfig.name }} on behalf of
          {{ ownerName }}, the operator of the berth.
        </p>
      </footer>
    </article>
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState v-else icon="bookings" title="Booking not found" description="The link may be wrong.">
      <template #action>
        <Button :to="routes.bookings">All bookings</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--space-2xs);
  max-width: 50rem;
  margin: 0 auto var(--space-sm);
}

.receipt {
  max-width: 50rem;
  margin-inline: auto;
  padding: clamp(var(--space-md), 5vw, var(--space-xl));
  color: var(--color-text);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  font-size: var(--text-sm);
}

.head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-sm);
  padding-bottom: var(--space-md);
  border-bottom: 2px solid var(--color-heading);
}

.title {
  text-align: right;
}

@media (max-width: 40rem) {
  .title {
    text-align: left;
  }
}

.title h1 {
  font-size: var(--text-lg);
  color: var(--color-heading);
}

.ref {
  font-family: var(--font-mono);
  color: var(--color-text-muted);
}

.parties {
  overflow-wrap: anywhere;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: var(--space-md);
  padding-block: var(--space-md);
}

.caption,
.meta dt {
  margin-bottom: var(--space-3xs);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.name {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.muted {
  color: var(--color-text-muted);
}

.meta {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
  margin: 0;
}

.meta dt {
  margin-bottom: 0;
}

.meta dd {
  margin: 0;
  color: var(--color-heading);
}

.lines {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}

.lines th,
.lines td {
  padding: var(--space-2xs) 0;
  text-align: left;
  vertical-align: top;
}

.lines thead th {
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--color-text-muted);
  border-bottom: var(--border-width) solid var(--color-border-strong);
}

.lines tbody td {
  padding-block: var(--space-sm);
  border-bottom: var(--border-width) solid var(--color-border);
}

.lines .num {
  padding-left: var(--space-sm);
  text-align: right;
  white-space: nowrap;
}

.lines tfoot th {
  font-weight: var(--weight-regular);
  text-align: right;
  color: var(--color-text-muted);
}

.lines tfoot .total th,
.lines tfoot .total td {
  padding-top: var(--space-2xs);
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  color: var(--color-heading);
  border-top: 2px solid var(--color-heading);
}

.item {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.foot {
  margin-top: var(--space-lg);
  padding-top: var(--space-sm);
  border-top: var(--border-width) solid var(--color-border);
}

.small {
  margin-top: var(--space-2xs);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

@media print {
  .noPrint {
    display: none !important;
  }

  .receipt {
    max-width: none;
    padding: 0;
    color: #000;
    background: #fff;
    border: 0;
    box-shadow: none;
  }
}
</style>

<!-- Unscoped on purpose: it has to reach the shell around the page. Keyed on
     the receipt being present, so it does nothing on any other screen. -->
<style>
@media print {
  @page {
    margin: 18mm;
  }

  body:has(.receipt) {
    background: #fff !important;
  }

  body:has(.receipt) .sidebar,
  body:has(.receipt) .topbar,
  body:has(.receipt) .skip-link,
  body:has(.receipt) .backdrop {
    display: none !important;
  }

  body:has(.receipt) .shell {
    display: block !important;
    min-height: 0 !important;
    background: #fff !important;
  }

  body:has(.receipt) .content {
    padding: 0 !important;
  }
}
</style>
